<template>

    <v-container class="pb-0">

        <v-row align="center" justify="center" v-show="questionsLoading">
            <v-progress-circular
                :size="100"
                :width="15"
                :value="50"
                color="primary"
                indeterminate
            ></v-progress-circular>
        </v-row>

        <v-card flat v-show="!questionsLoading" :color="backgroundColor" class="mb-10">

            <v-card-text>

                <v-alert type="info" :color="buttonColor" :class="'body-2 ' + getFontClasses(buttonColor)">
                    {{ translations.dynamic_profiling_header }}
                </v-alert>

                <v-progress-linear v-show="!questionsLoading"
                                   striped
                                   rounded
                                   height="20"
                                   :value="progress"
                                   :color="buttonColor"
                >
                    <template v-slot="{ value }">
                        <strong :class="getFontClasses(buttonColor)">{{ progress }}%</strong>
                    </template>

                </v-progress-linear>

                <component v-if="activeQuestion"
                           class="mt-5"
                           v-bind:is="ucFirst(activeQuestion.type) + 'Question'"
                           :variable="activeQuestion"
                           :translations="translations"
                           :locale="locale"
                           :key="activeQuestion.uuid"
                           :background-color="backgroundColor"
                           :button-color="buttonColor"
                           :loading="saving"
                           @input="saveAnswer"
                           @skip="questionSkipped"
                ></component>

            </v-card-text>

            <json v-if="debug" :data="translations"></json>

        </v-card>
    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import CheckboxQuestion from "../DynamicProflingQuestions/CheckboxQuestion";
import DateQuestion from "../DynamicProflingQuestions/DateQuestion";
import EmailQuestion from "../DynamicProflingQuestions/EmailQuestion";
import KeywordQuestion from "../DynamicProflingQuestions/KeywordQuestion";
import LocaleQuestion from "../DynamicProflingQuestions/LocaleQuestion";
import NumberQuestion from "../DynamicProflingQuestions/NumberQuestion";
import RadioQuestion from "../DynamicProflingQuestions/RadioQuestion";
import TextQuestion from "../DynamicProflingQuestions/TextQuestion";
import PhoneQuestion from "../DynamicProflingQuestions/PhoneQuestion";

import axios from "axios";
import colorMixin from "../DynamicProflingQuestions/colorMixin";

export default {

    name: "DynamicProfiling",

    mixins: [colorMixin],

    components: {
        Json,
        CheckboxQuestion,
        DateQuestion,
        EmailQuestion,
        KeywordQuestion,
        LocaleQuestion,
        NumberQuestion,
        RadioQuestion,
        TextQuestion,
        PhoneQuestion
    },

    props: {

        settings: {
            type: Object,
            required: true,
        },
        buttonColor: {
            type: String,
            required: false,
        },
        backgroundColor: {
            type: String,
            required: false,
        },
        editMode: {
            type: Boolean,
            required: true,
        },
        translations: {
            type: Object,
            required: true,
        },
        headerText: {
            type: String | null,
            required: false,
            default: null
        }
    },

    data() {
        return {
            debug: false,
            activeQuestion: null,
            questions: [],
            questionsLoading: false,
            originalQuestionCount: 0,
            saving: false,
            locale: null,
        }
    },

    computed: {
        progress() {
            return Math.round((this.originalQuestionCount - this.questions.length - 1) / this.originalQuestionCount * 100);
        }
    },

    methods: {

        setActiveQuestion() {
            if (this.questions.length > 0) {
                this.activeQuestion = this.questions.splice(0, 1)[0];
            } else {
                this.$emit('completed', true);
            }
        },

        questionSkipped() {
            this.setActiveQuestion();
        },

        saveAnswer(answer) {

            const data = {
                dataVariableId: this.activeQuestion.id,
                answer: answer
            };

            this.saving = true;

            axios.post('/dynamic-profiling/answer', data)

                .then((response) => {

                    if (response.data.compensation && response.data.compensation > 0) {
                        this.$emit('reward', response.data.compensation);
                    }

                    // TODO Must come from translations
                    Bus.$emit('flash-message', {
                        type: 'info',
                        message: 'Thanks for your answer!'
                    });

                    this.$emit('remaining', this.questions.length);

                    this.setActiveQuestion();
                })
                .catch(error => {

                    if (error.response.status === 409) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Answer has already been provided to this question!'
                        });
                    } else if (error.response.status === 419) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Security token mismatch! Please reload!'
                        });
                    } else if (error.response.status === 422) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Invalid answer format!'
                        });
                    } else {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Oops! That failed (' + error.response.status + ') please try again later!'
                        });
                    }
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },

    created() {

        if (!this.editMode) {

            // Dynamic profiling only available on these landing pages
            this.questionsLoading = true;

            axios.get('/dynamic-profiling/questions')

                .then(({data}) => {

                    this.questions = data.questions;
                    this.locale = data.locale;

                    this.originalQuestionCount = data.questions.length;

                    this.$emit('remaining', this.originalQuestionCount);

                    this.setActiveQuestion();
                })
                .catch(error => {

                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'Oops! That failed please try again later!'
                    });
                })
                .finally(() => {
                    this.questionsLoading = false;
                });
        }
    }

}
</script>